import { type ForwardRefExoticComponent, Fragment, forwardRef, useMemo } from 'react';

import { isChildrenEmpty } from '../../utils/is-children-empty';
import { AvatarBadge } from './avatar-badge';
import { AvatarFullName } from './avatar-full-name';
import { AvatarIcon } from './avatar-icon';
import { AvatarImage } from './avatar-image';
import { AvatarRoot } from './avatar-root';
import type { AvatarPropsInterface } from './types/avatar.types';

/**
 * @since 6.10.0
 */

export const Avatar = forwardRef<HTMLDivElement, AvatarPropsInterface>((props, forwardedRef) => {
  const { icon, src, alt, badge, fullName, children, ...rest } = props;

  const content = useMemo(() => {
    // Avatar template made out of composable components and dynamic children content
    const template = () => (
      <Fragment>
        {icon ? (
          <Avatar.Icon>{icon}</Avatar.Icon>
        ) : src && alt ? (
          <Avatar.Image src={src} alt={alt} />
        ) : fullName ? (
          <Avatar.FullName>{fullName}</Avatar.FullName>
        ) : null}
      </Fragment>
    );

    if (isChildrenEmpty(children)) {
      if (!fullName && !src && !alt && !icon) {
        return <Avatar.Icon />;
      }

      return template();
    }

    return children;
  }, [children, fullName, icon, src, alt]);

  return (
    <Avatar.Root ref={forwardedRef} {...rest}>
      {content}
      {badge ?? null}
    </Avatar.Root>
  );
}) as ForwardRefExoticComponent<AvatarPropsInterface> & {
  Root: typeof AvatarRoot;
  Badge: typeof AvatarBadge;
  Icon: typeof AvatarIcon;
  Image: typeof AvatarImage;
  FullName: typeof AvatarFullName;
};

Avatar.displayName = 'Avatar';

Avatar.Root = AvatarRoot;
Avatar.Badge = AvatarBadge;
Avatar.Icon = AvatarIcon;
Avatar.Image = AvatarImage;
Avatar.FullName = AvatarFullName;
